import React from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Camera, Save, Edit3 } from 'lucide-react';

interface FeedbackCaptureModalProps {
  open: boolean;
  blob: Blob | null;
  onCancel: () => void;
  onSaveDraft: () => void;
  onAddDetails: () => void;
  saving?: boolean;
}

export const FeedbackCaptureModal: React.FC<FeedbackCaptureModalProps> = ({
  open,
  blob,
  onCancel,
  onSaveDraft,
  onAddDetails,
  saving = false,
}) => {
  const imageUrl = React.useMemo(() => {
    if (!blob) return undefined;
    return URL.createObjectURL(blob);
  }, [blob]);

  // Clean up Object URL
  React.useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onCancel()}>
      <DialogContent className="max-w-[600px] flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" />
            <Trans>Screenshot Captured</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center bg-muted/30 p-4 rounded-lg border border-border">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt="Captured" 
              className="max-h-[450px] rounded-md shadow-sm object-contain"
            />
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              <Trans>No image data</Trans>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-row justify-end space-x-2 sm:space-x-2 mt-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} className="flex-1">
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="secondary" onClick={onSaveDraft} disabled={saving || !blob} className="flex-1">
            <Save className="w-4 h-4 mr-1.5" />
            <Trans>Save as Draft</Trans>
          </Button>
          <Button onClick={onAddDetails} disabled={saving || !blob} className="flex-1">
            <Edit3 className="w-4 h-4 mr-1.5" />
            <Trans>Add Details</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
